import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Cpu, Search, Building2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_MAP = {
  active: { label: "Aktif", className: "bg-green-100 text-green-700" },
  maintenance: { label: "Bakımda", className: "bg-yellow-100 text-yellow-700" },
  inactive: { label: "Pasif", className: "bg-gray-100 text-gray-700" },
};

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [collapsedHospitals, setCollapsedHospitals] = useState({});
  const [formData, setFormData] = useState({
    hospital_id: "",
    device_name: "",
    device_serial: "",
    installation_date: "",
    status: "active",
  });

  useEffect(() => {
    fetchDevices();
    fetchHospitals();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${API}/devices`);
      setDevices(response.data);
    } catch (error) {
      console.error("Error fetching devices:", error);
      toast.error("Cihazlar yüklenirken hata oluştu");
    }
  };

  const fetchHospitals = async () => {
    try {
      const response = await axios.get(`${API}/hospitals`);
      setHospitals(response.data);
    } catch (error) {
      console.error("Error fetching hospitals:", error);
    }
  };

  const getHospitalName = (hospitalId) => {
    const hospital = hospitals.find((h) => h.id === hospitalId);
    return hospital ? hospital.name : "-";
  };

  const getHospitalCity = (hospitalId) => {
    const hospital = hospitals.find((h) => h.id === hospitalId);
    return hospital ? hospital.city : "";
  };

  // Filter and group devices by hospital
  const groupedDevices = useMemo(() => {
    let filtered = devices;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d =>
        d.device_name.toLowerCase().includes(term) ||
        d.device_serial.toLowerCase().includes(term) ||
        getHospitalName(d.hospital_id).toLowerCase().includes(term)
      );
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter(d => d.status === filterStatus);
    }

    // Group by hospital
    const groups = {};
    filtered.forEach(device => {
      const hId = device.hospital_id;
      if (!groups[hId]) {
        const hospital = hospitals.find(h => h.id === hId);
        groups[hId] = {
          hospitalId: hId,
          hospitalName: hospital?.name || "Bilinmeyen Hastane",
          hospitalCity: hospital?.city || "",
          devices: [],
        };
      }
      groups[hId].devices.push(device);
    });

    // Sort hospitals alphabetically, then devices by name
    return Object.values(groups)
      .sort((a, b) => a.hospitalName.localeCompare(b.hospitalName, 'tr'))
      .map(group => ({
        ...group,
        devices: group.devices.sort((a, b) => a.device_name.localeCompare(b.device_name, 'tr')),
      }));
  }, [devices, hospitals, searchTerm, filterStatus]);

  const toggleHospitalCollapse = (hospitalId) => {
    setCollapsedHospitals(prev => ({
      ...prev,
      [hospitalId]: !prev[hospitalId],
    }));
  };

  const activeCount = devices.filter(d => d.status === "active").length;
  const maintenanceCount = devices.filter(d => d.status === "maintenance").length;
  const inactiveCount = devices.filter(d => d.status === "inactive").length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDevice) {
        await axios.put(`${API}/devices/${editingDevice.id}`, formData);
        toast.success("Cihaz güncellendi");
      } else {
        await axios.post(`${API}/devices`, formData);
        toast.success("Cihaz eklendi");
      }
      fetchDevices();
      handleCloseDialog();
    } catch (error) {
      toast.error("Cihaz kaydedilirken hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu cihazı silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/devices/${id}`);
      toast.success("Cihaz silindi");
      fetchDevices();
    } catch (error) {
      toast.error("Cihaz silinirken hata oluştu");
    }
  };

  const handleEdit = (device) => {
    setEditingDevice(device);
    setFormData({
      hospital_id: device.hospital_id,
      device_name: device.device_name,
      device_serial: device.device_serial,
      installation_date: device.installation_date,
      status: device.status,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingDevice(null);
    setFormData({ hospital_id: "", device_name: "", device_serial: "", installation_date: "", status: "active" });
  };

  return (
    <div className="space-y-6" data-testid="devices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="devices-title">
            Cihazlar
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            {groupedDevices.length} hastanede toplam {devices.length} cihaz
          </p>
        </div>
        <Button onClick={() => { handleCloseDialog(); setIsDialogOpen(true); }} data-testid="add-device-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Cihaz
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600 font-medium">Toplam Cihaz</p>
            <p className="text-2xl font-bold text-blue-700 mt-1">{devices.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-green-600 font-medium">Aktif</p>
            <p className="text-2xl font-bold text-green-700 mt-1">{activeCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <p className="text-sm text-yellow-600 font-medium">Bakımda</p>
            <p className="text-2xl font-bold text-yellow-700 mt-1">{maintenanceCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600 font-medium">Pasif</p>
            <p className="text-2xl font-bold text-gray-700 mt-1">{inactiveCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cihaz adı, seri no veya hastane ara..."
                data-testid="device-search"
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36" data-testid="device-status-filter">
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="maintenance">Bakımda</SelectItem>
                <SelectItem value="inactive">Pasif</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Grouped Device Table */}
      {groupedDevices.map((group) => {
        const isCollapsed = collapsedHospitals[group.hospitalId];
        return (
          <Card key={group.hospitalId} data-testid={`hospital-group-${group.hospitalId}`}>
            <CardHeader
              className="cursor-pointer hover:bg-secondary/50 transition-colors py-4"
              onClick={() => toggleHospitalCollapse(group.hospitalId)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isCollapsed
                    ? <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    : <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  }
                  <Building2 className="w-5 h-5 text-blue-600" />
                  <div>
                    <CardTitle className="text-base">{group.hospitalName}</CardTitle>
                    <p className="text-xs text-muted-foreground font-normal mt-0.5">{group.hospitalCity}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                    {group.devices.length} cihaz
                  </span>
                </div>
              </div>
            </CardHeader>
            {!isCollapsed && (
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-4 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Cihaz Adı</th>
                        <th className="text-left py-2 px-4 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Seri No</th>
                        <th className="text-left py-2 px-4 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Kurulum Tarihi</th>
                        <th className="text-center py-2 px-4 font-semibold text-xs text-muted-foreground uppercase tracking-wider">Durum</th>
                        <th className="text-right py-2 px-4 font-semibold text-xs text-muted-foreground uppercase tracking-wider">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.devices.map((device) => {
                        const statusInfo = STATUS_MAP[device.status] || STATUS_MAP.inactive;
                        return (
                          <tr
                            key={device.id}
                            className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors"
                            data-testid={`device-row-${device.id}`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <Cpu className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                <span className="font-medium">{device.device_name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-mono text-sm text-muted-foreground">{device.device_serial}</span>
                            </td>
                            <td className="py-3 px-4 text-muted-foreground">
                              {device.installation_date
                                ? new Date(device.installation_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
                                : "-"}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusInfo.className}`}>
                                {statusInfo.label}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleEdit(device); }}
                                  data-testid={`edit-device-${device.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleDelete(device.id); }}
                                  data-testid={`delete-device-${device.id}`}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      {groupedDevices.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center" data-testid="no-devices">
              <Cpu className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm || filterStatus !== "all" ? "Filtreye uygun cihaz bulunamadı" : "Henüz cihaz eklenmemiş"}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="device-dialog">
          <DialogHeader>
            <DialogTitle>{editingDevice ? "Cihaz Düzenle" : "Yeni Cihaz Ekle"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="hospital_id">Hastane *</Label>
                <Select
                  value={formData.hospital_id}
                  onValueChange={(value) => setFormData({ ...formData, hospital_id: value })}
                  required
                >
                  <SelectTrigger data-testid="device-hospital-select">
                    <SelectValue placeholder="Hastane seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {hospitals.sort((a, b) => a.name.localeCompare(b.name, 'tr')).map((hospital) => (
                      <SelectItem key={hospital.id} value={hospital.id}>
                        {hospital.name} - {hospital.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="device_name">Cihaz Adı *</Label>
                <Input
                  id="device_name"
                  data-testid="device-name-input"
                  value={formData.device_name}
                  onChange={(e) => setFormData({ ...formData, device_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="device_serial">Seri Numarası *</Label>
                <Input
                  id="device_serial"
                  data-testid="device-serial-input"
                  value={formData.device_serial}
                  onChange={(e) => setFormData({ ...formData, device_serial: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="installation_date">Kurulum Tarihi *</Label>
                <Input
                  id="installation_date"
                  type="date"
                  data-testid="device-installation-date-input"
                  value={formData.installation_date}
                  onChange={(e) => setFormData({ ...formData, installation_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="status">Durum *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                  required
                >
                  <SelectTrigger data-testid="device-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="inactive">Pasif</SelectItem>
                    <SelectItem value="maintenance">Bakımda</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseDialog} data-testid="cancel-device-btn">İptal</Button>
              <Button type="submit" data-testid="save-device-btn">{editingDevice ? "Güncelle" : "Kaydet"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Devices;
